import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { getCityName } from '../../utils/flightDataParser';
import ContactSupportModal from './ContactSupportModal';
import { generateInvoice } from '../../services/flightBookingAPI';
import { auth } from '../../firebase';

function BookingConfirmation({ onContactSupport, trace_id, result_idx, flightDetails, bookingData }) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const navigate = useNavigate();


  const safeParse = (data) => {
    if (!data) return {};
    if (typeof data === "object") return data;
    try {
      const first = JSON.parse(data);
      if (typeof first === "string") return JSON.parse(first);
      return first;
    } catch {
      return {};
    }
  };

  // useEffect(() => {
  //   console.log('BookingConfirmation received:', { trace_id, result_idx, flightDetails, bookingData });
  // }, [trace_id, result_idx, flightDetails, bookingData]);

  /**
   * Handle ticket download - generates PDF on client side
   */
  const handleDownloadTicket = async () => {
    if (isDownloading) return;

    try {
      setIsDownloading(true);

      const ticketDetails = safeParse(bookingData?.ticket_details);
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || bookingData?.user_id || bookingData?.email || "anon").trim();

      const payload = {
        ticket_details: ticketDetails,
        platform_fee: bookingData?.rc_platform_fee || 0,
        gst_rate: bookingData?.rc_gst_rate || 0,
        rc_booking_id: bookingData?.rc_booking_id,
        user_id: userIdentifier,
        document_type: 'ticket'
      };

      const response = await generateInvoice(payload);
      const url = response.ticket_url || response.invoice_url;
      if (response.status === "success" && url) {
        const link = document.createElement('a');
        link.href = url;
        link.download = `ticket_${bookingData?.rc_booking_id || 'download'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.message || "Failed to generate ticket");
      }
    } catch (error) {
      console.error('[BookingConfirmation] Error generating ticket:', error);
      alert('Failed to generate ticket PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  /**
   * Handle invoice download - downloads invoice from URL
   */
  const handleDownloadInvoice = async () => {
    if (isGeneratingInvoice) return;

    try {
      setIsGeneratingInvoice(true);

      const ticketDetails = safeParse(bookingData?.ticket_details);
      const user = auth.currentUser;
      const userIdentifier = (user?.email || user?.phoneNumber?.replace("+91", "") || bookingData?.user_id || bookingData?.email || "anon").trim();

      const payload = {
        ticket_details: ticketDetails,
        platform_fee: bookingData?.rc_platform_fee || 0,
        gst_rate: bookingData?.rc_gst_rate || 0,
        rc_booking_id: bookingData?.rc_booking_id,
        user_id: userIdentifier
      };

      const response = await generateInvoice(payload);
      if (response.status === "success" && response.invoice_url) {
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = response.invoice_url;
        link.download = `invoice_${bookingData.rc_booking_id || 'download'}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.message || "Failed to generate invoice");
      }
    } catch (error) {
      console.error("Invoice generation error:", error);
      alert("Failed to generate or download invoice. Please try again.");
    } finally {
      setIsGeneratingInvoice(false);
    }
  };
  // Determine effective flight details by searching through possible nesting levels
  const getEffectiveFlightDetails = () => {
    // Priority 1: Use flightDetails prop if it actually contains segments
    if (flightDetails && (flightDetails.Segments || flightDetails.Passenger)) {
      return flightDetails;
    }

    // Priority 2: Try to find it inside ticket_details or Response
    const ticketDetails = safeParse(bookingData?.ticket_details);
    const bookingRequest = safeParse(bookingData?.booking_request);

    const candidates = [
      flightDetails,
      ticketDetails?.FlightItinerary,
      ticketDetails?.Response?.Response?.FlightItinerary,
      ticketDetails?.Response?.FlightItinerary,
      bookingRequest?.FlightItinerary,
      bookingRequest?.Response?.Response?.FlightItinerary,
      bookingRequest?.Response?.FlightItinerary,
      bookingData?.FlightItinerary,
      bookingData?.Response?.Response?.FlightItinerary,
      bookingData?.Response?.FlightItinerary,
      bookingData?.ticket_details?.Response?.FlightItinerary
    ];

    for (const cand of candidates) {
      if (cand && (cand.Segments || cand.Passenger || cand.PNR)) {
        return cand;
      }
    }

    return flightDetails || {};
  };

  const effectiveFlightDetails = getEffectiveFlightDetails();

  // Extract data from effectiveFlightDetails
  const pnr = effectiveFlightDetails?.PNR || bookingData?.pnr || 'N/A';
  const passengers = effectiveFlightDetails?.Passenger || [];
  const leadPassenger = passengers.find(p => p.IsLeadPax) || passengers[0] || {};

  // Handle segments which can be nested in some TBO responses
  let segments = effectiveFlightDetails?.Segments || [];
  if (segments.length > 0 && Array.isArray(segments[0])) {
    segments = segments[0];
  }
  const firstSegment = segments[0] || {};
  const lastSegment = segments[segments.length - 1] || firstSegment;

  // Fare handling with fallback to bookingData
  const fare = effectiveFlightDetails?.Fare || {};
  const totalAmount = fare?.PublishedFare || fare?.OfferedFare || bookingData?.amount || 0;
  const currency = fare?.Currency || bookingData?.currency || 'INR';

  // Promo Logic
  const promoAmount = Number(bookingData?.applied_promo_discount || 0);
  const promoCode = bookingData?.applied_promo || "";
  let promoDescription = "";
  if (promoCode) {
    const validPromos = safeParse(bookingData?.valid_promo_codes);
    promoDescription = validPromos?.[promoCode]?.desc || "";
  }
  let remainingDiscountForFlight = promoAmount;
  let effectivePlatformFee = Number(bookingData?.rc_platform_fee || 0);
  let displayGstAmount = (effectivePlatformFee * Number(bookingData?.rc_gst_rate || 0)) / 100;

  if (promoAmount > 0) {
    if (effectivePlatformFee > 0) {
      let feeDiscount = Math.min(effectivePlatformFee, promoAmount);
      effectivePlatformFee = effectivePlatformFee - feeDiscount;
      remainingDiscountForFlight = promoAmount - feeDiscount;
      displayGstAmount = (effectivePlatformFee * Number(bookingData?.rc_gst_rate || 0)) / 100;
    }
  }

  const bookingStatus = (effectiveFlightDetails?.Status === 1 || effectiveFlightDetails?.Status === 5 || bookingData?.status === "TICKET_ISSUED" || bookingData?.status === "SUCCESS") ? 'Confirmed' : 'Pending';

  // Fallback for missing passenger info using bookingData
  const bookingEmail = bookingData?.user_id || bookingData?.email || "";
  const displayEmail = leadPassenger?.Email || (bookingEmail.includes('@') ? bookingEmail : null) || 'N/A';
  const displayPhone = leadPassenger?.ContactNo || bookingData?.phone || 'N/A';

  // Format dates
  const formatTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return 'N/A';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}hrs ${mins}mins`;
  };

  /**
   * Calculate layover time in minutes between two segments
   */
  const calculateLayoverTime = (currentSegment, nextSegment) => {
    if (!currentSegment || !nextSegment) return 0;
    const arrivalTime = new Date(currentSegment.Destination.ArrTime);
    const departureTime = new Date(nextSegment.Origin.DepTime);
    const diffInMs = departureTime - arrivalTime;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    return diffInMinutes > 0 ? diffInMinutes : 0;
  };

  /**
   * Calculate total trip duration by summing segment durations and layovers
   */
  const calculateTotalTripDuration = (segs) => {
    if (!segs || segs.length === 0) return 0;
    let total = 0;
    for (let i = 0; i < segs.length; i++) {
      total += (segs[i].Duration || 0);
      if (i < segs.length - 1) {
        total += calculateLayoverTime(segs[i], segs[i + 1]);
      }
    }
    return total;
  };

  const tripTotalDuration = calculateTotalTripDuration(segments);

  /**
   * Calculate total duration between two dates in minutes (fallback method)
   */
  const calculateTotalDuration = (start, end) => {
    if (!start || !end) return 0;
    const diffMs = new Date(end) - new Date(start);
    return Math.floor(diffMs / (1000 * 60));
  };

  const totalFlightDuration = segments.length > 0
    ? (tripTotalDuration || calculateTotalDuration(firstSegment?.Origin?.DepTime, lastSegment?.Destination?.ArrTime))
    : 0;


  const getPaxTypeLabel = (paxType) => {
    switch (paxType) {
      case 1: return 'Adult';
      case 2: return 'Child';
      case 3: return 'Infant';
      default: return 'Adult';
    }
  };

  /**
   * Generates and downloads an .ics file for the flight
   */
  const handleAddToCalendar = useCallback(() => {
    try {
      const airName = firstSegment?.Airline?.AirlineName || "Flight";
      const flNo = firstSegment?.Airline?.FlightNumber || "";
      const org = getCityName(firstSegment?.Origin?.Airport);
      const dest = getCityName(lastSegment?.Destination?.Airport);

      const formatICSDate = (dateStr) => {
        if (!dateStr) return "";
        return new Date(dateStr).toISOString().replace(/-|:|\.\d+/g, "");
      };

      const start = formatICSDate(firstSegment?.Origin?.DepTime);
      const end = formatICSDate(lastSegment?.Destination?.ArrTime);

      if (!start || !end) {
        alert("Flight times not available for calendar.");
        return;
      }

      const icsMsg = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        "BEGIN:VEVENT",
        `SUMMARY:Flight ${airName} ${flNo} (${org} -> ${dest})`,
        `DTSTART:${start}`,
        `DTEND:${end}`,
        `LOCATION:${firstSegment?.Origin?.Airport?.AirportName || org}`,
        `DESCRIPTION:Confirmation PNR: ${pnr}\\nFlight: ${airName} ${flNo}\\nBooking ID: ${bookingData?.rc_booking_id || "N/A"}`,
        "STATUS:CONFIRMED",
        "END:VEVENT",
        "END:VCALENDAR"
      ].join("\r\n");

      const blob = new Blob([icsMsg], { type: "text/calendar;charset=utf-8" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.setAttribute("download", `flight_${pnr}.ics`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Calendar error:", err);
    }
  }, [firstSegment, lastSegment, pnr, bookingData]);

  /**
   * Share booking details via Web Share API
   */
  const handleShareBooking = useCallback(async () => {
    const org = getCityName(firstSegment?.Origin?.Airport);
    const dest = getCityName(lastSegment?.Destination?.Airport);
    const air = firstSegment?.Airline?.AirlineName || "Airline";

    const shareText = `My flight ${air} (${org} to ${dest}) is confirmed! PNR: ${pnr}. Booked via RouteChef.`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Flight Confirmation',
          text: shareText,
          url: window.location.origin
        });
      } catch (err) {
        console.error('Share canceled or failed', err);
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(shareText);
        alert('Booking details copied to clipboard!');
      } catch (err) {
        alert('PNR: ' + pnr);
      }
    }
  }, [firstSegment, lastSegment, pnr]);

  return (
    <div className="max-w-[1360px] mx-auto space-y-8 md:space-y-12 mt-8 md:mt-16 mb-20 px-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
      {/* Success Message Header */}
      <div className="text-center space-y-6 p-6 bg-white rounded-xl">
        <div className="flex justify-center">
          <svg width="71" height="71" viewBox="0 0 71 71" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16.9997 35.9996L26.1662 46.6134C26.3493 46.8254 26.6706 46.8457 26.8787 46.6581C29.2737 44.4979 42.5299 32.5168 52.4997 22.9996M69.5 35.5C69.5 54.2777 54.2777 69.5 35.5 69.5C16.7223 69.5 1.5 54.2777 1.5 35.5C1.5 16.7223 16.7223 1.5 35.5 1.5C54.2777 1.5 69.5 16.7223 69.5 35.5Z" stroke="#00C950" stroke-width="3" stroke-linecap="round" />
          </svg>

        </div>

        <div className="space-y-2">
          <h1 className="text-[24px] font-medium text-[#0F172A]">Your flight is booked!</h1>
          <p className="text-[#94A3B8] text-lg font-medium">Confirmation details have been sent to your email</p>
        </div>

        <div className="relative px-0 md:px-20">
          <div className="bg-[#E7F9F1] rounded-2xl py-8 px-6 text-center relative overflow-hidden h-[240px] flex flex-col items-center justify-center">
            {/* Edge Cutouts */}
            <div className="hidden sm:block absolute left-[-50px] top-1/2 -translate-y-1/2 w-[100px] h-[110px] bg-white rounded-full z-10"></div>
            <div className="hidden sm:block absolute right-[-50px] top-1/2 -translate-y-1/2 w-[100px] h-[110px] bg-white rounded-full z-10"></div>

            {/* Decorative background circle */}
            <div className="absolute right-[5%] bottom-[-50px] w-64 h-64  blur-[2px]">
              <svg width="214" height="117" viewBox="0 0 214 117" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path opacity="0.44" d="M213.903 80.5317C211.754 67.8136 206.875 55.6847 199.546 44.8376C192.217 33.9906 182.581 24.6377 171.188 17.3131C159.795 9.98849 146.868 4.83555 133.146 2.14851C119.423 -0.538534 105.174 -0.707066 91.2112 1.65254C77.2484 4.01214 63.8456 8.85366 51.7681 15.9007C39.6906 22.9477 29.175 32.0622 20.8215 42.7238C12.4681 53.3854 6.44043 65.3853 3.08275 78.0383C-0.274938 90.6913 -0.896903 103.75 1.25236 116.468L107.578 98.4998L213.903 80.5317Z" fill="white" />
              </svg>


            </div>

            <div className="flex flex-col items-center space-y-4 relative z-20">
              <svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="22" cy="22" r="22" fill="white" />
                <path d="M32.0001 15.0798C32.0009 14.4701 31.8207 13.8737 31.4823 13.3665C31.144 12.8592 30.6626 12.4638 30.0993 12.2303C29.536 11.9968 28.916 11.9358 28.318 12.055C27.72 12.1742 27.1708 12.4682 26.7401 12.8998L23.9301 15.7098L17.4401 13.5498C17.2673 13.4955 17.083 13.4892 16.9068 13.5314C16.7307 13.5737 16.5694 13.663 16.4401 13.7898L14.0601 16.1498C13.9473 16.2639 13.8636 16.4034 13.8161 16.5567C13.7686 16.7099 13.7586 16.8723 13.7871 17.0301C13.8155 17.188 13.8815 17.3367 13.9795 17.4637C14.0775 17.5907 14.2046 17.6923 14.3501 17.7598L19.5301 20.1098L16.9301 22.7098L15.2201 21.8498C15.0354 21.7499 14.824 21.7106 14.6158 21.7375C14.4076 21.7645 14.2132 21.8562 14.0601 21.9998L12.2901 23.8098C12.1038 23.9972 11.9993 24.2506 11.9993 24.5148C11.9993 24.779 12.1038 25.0325 12.2901 25.2198L18.7801 31.7098C18.9674 31.8961 19.2209 32.0006 19.4851 32.0006C19.7492 32.0006 20.0027 31.8961 20.1901 31.7098L22.0001 29.9398C22.1501 29.7913 22.2494 29.5991 22.2835 29.3907C22.3176 29.1823 22.2849 28.9685 22.1901 28.7798L21.3301 27.0698L23.9301 24.4698L26.2801 29.6498C26.3476 29.7953 26.4492 29.9224 26.5762 30.0204C26.7032 30.1184 26.8519 30.1844 27.0098 30.2128C27.1676 30.2413 27.33 30.2313 27.4832 30.1838C27.6364 30.1363 27.776 30.0526 27.8901 29.9398L30.2501 27.5798C30.3769 27.4505 30.4662 27.2892 30.5085 27.1131C30.5507 26.9369 30.5444 26.7526 30.4901 26.5798L28.3301 20.0898L31.1001 17.2598C31.3873 16.9744 31.6148 16.6346 31.7693 16.2603C31.9238 15.8861 32.0023 15.4848 32.0001 15.0798ZM29.6801 15.8498L26.4401 19.0898C26.3132 19.2192 26.2239 19.3805 26.1817 19.5566C26.1394 19.7327 26.1458 19.917 26.2001 20.0898L28.3601 26.5698L27.4601 27.4698L25.1101 22.2998C25.0434 22.153 24.9421 22.0245 24.815 21.9252C24.6879 21.826 24.5387 21.7589 24.3801 21.7298C24.2201 21.7006 24.0555 21.7109 23.9004 21.7597C23.7453 21.8085 23.6045 21.8943 23.4901 22.0098L19.3701 26.1698C19.2221 26.3176 19.1243 26.508 19.0902 26.7143C19.0561 26.9206 19.0875 27.1323 19.1801 27.3198L20.0001 28.9998L19.4401 29.5598L14.4101 24.5198L15.0001 23.9998L16.7101 24.8598C16.8975 24.9524 17.1093 24.9838 17.3156 24.9497C17.5219 24.9156 17.7123 24.8177 17.8601 24.6698L22.0001 20.5098C22.115 20.3958 22.2005 20.2556 22.2492 20.1013C22.298 19.947 22.3086 19.7831 22.2801 19.6237C22.2516 19.4644 22.1848 19.3144 22.0855 19.1866C21.9862 19.0587 21.8574 18.9569 21.7101 18.8898L16.5001 16.5398L17.4001 15.6398L23.8801 17.7998C24.0529 17.8541 24.2372 17.8605 24.4133 17.8182C24.5894 17.776 24.7507 17.6867 24.8801 17.5598L28.1201 14.3198C28.2197 14.2179 28.3387 14.137 28.4701 14.0817C28.6014 14.0264 28.7425 13.9979 28.8851 13.9979C29.0276 13.9979 29.1687 14.0264 29.3 14.0817C29.4314 14.137 29.5504 14.2179 29.6501 14.3198C29.7544 14.4165 29.8369 14.5343 29.892 14.6653C29.9472 14.7964 29.9738 14.9377 29.9701 15.0798C29.9755 15.221 29.9526 15.3619 29.9028 15.4941C29.853 15.6264 29.7773 15.7473 29.6801 15.8498Z" fill="#FF4E01" />
              </svg>


              <span className="text-[18px] font-medium text-[#0F172A]">Booking Reference</span>

              <div className="text-[32px] font-medium text-[#0F172A] leading-none tracking-tight">
                {pnr}
              </div>

              <div className="px-6 py-2 bg-[#059669] text-white text-[15px] font-medium rounded-[8px] leading-none flex items-center justify-center min-h-[36px]">
                {bookingStatus}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Reference Banner */}


      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Left Column - Flight Details */}
        <div className="lg:col-span-7 space-y-8">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-[#FFF9F5] px-6 py-5 flex items-center gap-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.9999 10.5V9.99995C18.9999 6.22876 18.9998 4.34311 17.8283 3.17154C16.6567 2 14.7711 2 10.9999 2C7.22883 2 5.3432 2.00006 4.17163 3.17159C3.00009 4.34315 3.00007 6.22872 3.00004 9.99988L3 14.5C2.99997 17.7874 2.99996 19.4312 3.90788 20.5375C4.07412 20.7401 4.25986 20.9258 4.46243 21.0921C5.56877 22 7.21249 22 10.4999 22" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M7 7H15M7 11H11" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M18 18.5L16.5 17.95V15.5M12 17.5C12 19.9853 14.0147 22 16.5 22C18.9853 22 21 19.9853 21 17.5C21 15.0147 18.9853 13 16.5 13C14.0147 13 12 15.0147 12 17.5Z" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

              <h2 className="text-lg font-semibold text-[#FF6500]">Booking Detail</h2>
            </div>

            <div className="p-5 md:p-8">
              <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
                <div className="flex items-center gap-1.5 px-3 py-1 bg-[#F1F5F9] rounded-lg overflow-x-auto max-w-full">
                  {segments.map((_, index) => (
                    <React.Fragment key={index}>
                      {index > 0 && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                          <path d="M4.23492 2.73492C4.37222 2.59762 4.58958 2.58889 4.73687 2.70904L4.7652 2.73492L7.7652 5.73492L7.79107 5.76324C7.90323 5.90072 7.90323 6.09939 7.79107 6.23687L7.7652 6.26519L4.7652 9.26519C4.61875 9.41164 4.38137 9.41164 4.23492 9.26519C4.08848 9.11875 4.08848 8.88137 4.23492 8.73492L6.96978 6.00006L4.23492 3.2652L4.20904 3.23687C4.08889 3.08958 4.09762 2.87222 4.23492 2.73492Z" fill="#475569" />
                        </svg>
                      )}
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                        <path d="M1.33325 13.3335H11.9999" stroke="#141B34" stroke-linecap="round" stroke-linejoin="round" />
                        <path d="M2.55006 8.11057C2.36672 7.97724 2.20006 7.81057 2.00006 7.22724C1.94582 7.08257 1.7408 6.3573 1.56673 5.6939C1.4199 5.1343 1.28906 4.62354 1.35006 4.4939C1.40007 4.36056 1.46672 4.26056 1.68339 4.12723C1.81672 4.04518 2.50006 3.87723 2.63339 3.8439C2.76672 3.81056 2.95005 3.7939 3.10006 3.8439C3.38339 3.8939 3.96672 4.7439 4.11672 4.8439C4.18338 4.91056 4.40006 5.1045 4.65006 5.12723C4.83339 5.1439 5.01672 5.0939 5.21673 5.01056C5.40006 4.93418 9.01674 3.17723 9.35008 3.02723C12.0667 1.8939 14.0401 3.75723 14.3401 4.15723C14.6501 4.5439 14.7167 4.66056 14.6334 4.9939C14.5257 5.3439 14.2334 5.41056 14.0667 5.46056C13.9001 5.51056 11.6001 6.12723 10.7001 6.37723C10.5035 6.44124 10.4075 6.56978 10.3834 6.5939C10.2667 6.76057 9.73674 7.8939 9.58674 8.1439C9.48341 8.41057 9.20008 8.7439 8.83341 8.87724C8.45008 9.01057 7.78341 9.16057 7.63341 9.21057C7.48341 9.26057 7.13341 9.36057 7.01674 9.32724C6.86674 9.2939 6.72341 9.1439 6.79008 8.91057C6.85674 8.67724 6.98341 8.02724 7.00008 7.92724C7.01675 7.82724 7.18341 7.41057 7.00008 7.3939C6.96675 7.3439 6.61673 7.4939 6.10006 7.61057C5.71621 7.71864 5.31421 7.82557 5.03339 7.8939C3.95006 8.21057 3.36335 8.2939 3.23339 8.2939C2.98339 8.2939 2.80006 8.26057 2.55006 8.11057Z" stroke="#141B34" />
                      </svg>
                    </React.Fragment>
                  ))}
                </div>
                <span className="px-3 py-1 bg-[#ECFDF5] text-[#059669] text-sm font-bold rounded-lg uppercase tracking-wider shadow-sm border border-[#D1FAE5]">Confirmed</span>
              </div>

              <div className="flex flex-col md:flex-row items-center justify-between mb-8 gap-6 md:gap-4 lg:gap-8">
                <div className="flex flex-col items-center md:items-start text-center md:text-left w-full md:w-auto min-w-fit">
                  <span className="text-[18px] md:text-[20px] font-bold text-[#0F172A] leading-tight">
                    {getCityName(firstSegment?.Origin?.Airport)} ({firstSegment?.Origin?.Airport?.AirportCode})
                  </span>
                  <span className="text-[17px] md:text-[18px] font-bold text-[#FF4E01] mt-1">{formatTime(firstSegment?.Origin?.DepTime)}</span>
                  <span className="text-sm font-medium text-[#64748B] mt-1">{formatDate(firstSegment?.Origin?.DepTime)}</span>
                </div>

                <div className="flex-1 flex flex-col items-center relative w-full max-w-[200px] md:max-w-none">
                  <div className="w-full border-t-2 border-dotted border-[#CBD5E1] absolute top-1/2 -translate-y-1/2"></div>
                  <div className="bg-white px-2 md:px-6 relative z-10 flex flex-col items-center">
                    <span className="text-[14px] font-bold text-[#475569]">{formatDuration(totalFlightDuration)}</span>
                    <div className="flex items-center gap-1.5 mt-0.5">

                      <span className="text-[12px] text-[#94A3B8] font-bold uppercase tracking-tight whitespace-nowrap">
                        {segments.length - 1 === 0 ? '0 change stop' : `${segments.length - 1} change stop`}
                      </span>

                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-center md:items-end text-center md:text-right w-full md:w-auto min-w-fit">
                  <span className="text-[18px] md:text-[20px] font-bold text-[#0F172A] leading-tight">
                    {getCityName(lastSegment?.Destination?.Airport)} ({lastSegment?.Destination?.Airport?.AirportCode})
                  </span>
                  <span className="text-[17px] md:text-[18px] font-bold text-[#FF4E01] mt-1">{formatTime(lastSegment?.Destination?.ArrTime)}</span>
                  <span className="text-sm font-medium text-[#64748B] mt-1">{formatDate(lastSegment?.Destination?.ArrTime)}</span>
                </div>
              </div>

              <hr className="border-[#CBD5E1] mb-6" />

              <div className="grid grid-cols-2 md:grid-cols-3 gap-y-6 gap-x-4">
                <div className="space-y-1">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Date</span>
                  <div className="text-[15px] font-semibold text-[#0F172A]">{firstSegment?.Origin?.DepTime ? new Date(firstSegment.Origin.DepTime).toLocaleDateString('en-CA') : 'N/A'}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Time Range</span>
                  <div className="text-[15px] font-semibold text-[#0F172A]">{formatTime(firstSegment?.Origin?.DepTime)} - {formatTime(lastSegment?.Destination?.ArrTime)}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Airline</span>
                  <div className="text-[15px] font-semibold text-[#0F172A]">{firstSegment?.Airline?.AirlineName}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Flight No</span>
                  <div className="text-[15px] font-semibold text-[#0F172A]">{firstSegment?.Airline?.FlightNumber || firstSegment?.Airline?.AirlineName}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Total Duration</span>
                  <div className="text-[15px] font-semibold text-[#0F172A]">{formatDuration(totalFlightDuration)}</div>
                </div>
                <div className="space-y-1">
                  <span className="text-[12px] font-bold text-slate-400 uppercase tracking-wider">Baggage Allowance</span>
                  <div className="text-[15px] font-semibold text-[#0F172A]">{firstSegment?.Baggage || 'Standard Baggage'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Summary - Moved here to fix white space */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-[#FFF9F5] px-6 py-5 flex items-center gap-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.4407 21.3478L16.9929 20.6576C17.4781 20.0511 18.5085 20.1 18.9184 20.7488C19.4114 21.5295 20.6292 21.3743 20.9669 20.6562C20.9845 20.6189 20.9405 20.7123 20.9781 20.3892C21.0156 20.0661 21 19.9923 20.9687 19.8448L19.0456 10.7641C18.5623 8.48195 18.3206 7.34086 17.4893 6.67043C16.6581 6 15.4848 6 13.1384 6H10.8616C8.51517 6 7.34194 6 6.51066 6.67043C5.67937 7.34086 5.43771 8.48195 4.95439 10.7641L3.0313 19.8448C3.00004 19.9923 2.98441 20.0661 3.02194 20.3892C3.05946 20.7123 3.01554 20.6189 3.0331 20.6562C3.37084 21.3743 4.58856 21.5295 5.08165 20.7488C5.49152 20.1 6.52187 20.0511 7.00709 20.6576L7.55934 21.3478C8.25514 22.2174 9.70819 22.2174 10.404 21.3478L10.4908 21.2392C11.2291 20.3165 12.7709 20.3165 13.5092 21.2392L13.596 21.3478C14.2918 22.2174 15.7449 22.2174 16.4407 21.3478Z" stroke="#FF4E01" stroke-width="1.5" stroke-linejoin="round" />
                <path d="M2.48336 9.5C1.89805 8.89199 2.00824 8.10893 2.00824 6.15176C2.00824 4.1946 2.00824 3.21602 2.59355 2.60801C3.17886 2 4.1209 2 6.00497 2H17.9952C19.8792 2 20.8213 2 21.4066 2.60801C21.9919 3.21602 21.9919 4.1946 21.9919 6.15176C21.9919 8.10893 22.1014 8.89199 21.5161 9.5" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" />
                <path d="M12 10H9" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M14 14H8" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <h2 className="text-lg font-semibold text-[#FF4E01]">Payment Summary</h2>
            </div>
            <div className="p-4 md:p-8 space-y-3">
              {/* Airfare (Base + taxes) from OfferedFare */}
              <div className="flex justify-between items-center text-[16px] font-medium text-[#475569]">
                <span>Airfare (Base + taxes)</span>
                <span>₹ {Number(fare?.PublishedFare || 0).toLocaleString('en-IN')}</span>
              </div>

              {/* Discount (if applicable) */}
              {fare?.Discount > 0 && (
                <div className="flex justify-between items-center text-[16px] font-medium text-[#475569]">
                  <span>Discount</span>
                  <span>₹ {Number(fare.Discount).toLocaleString('en-IN')}</span>
                </div>
              )}

              {/* Service Fee (if applicable) */}
              {fare?.ServiceFee > 0 && (
                <div className="flex justify-between items-center text-[16px] font-medium text-[#475569]">
                  <span>Service Fee</span>
                  <span>₹ {Number(fare.ServiceFee).toLocaleString('en-IN')}</span>
                </div>
              )}

              {/* Platform Fee & Promo Block wrapped in border */}
              {(bookingData?.rc_platform_fee > 0 || promoAmount > 0) && (
                <div>
                  {/* Platform Fee */}
                  {bookingData?.rc_platform_fee > 0 && (
                    <div className="flex justify-between items-center text-[14px] font-medium text-[#475569]">
                      <span>Platform Service Fee</span>
                      <span>₹ {Number(bookingData.rc_platform_fee).toLocaleString('en-IN')}</span>
                    </div>
                  )}

                  {/* Promo Code Discount */}
                  {promoAmount > 0 && (
                    <div className="group mt-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-3">
                          <div className="bg-[#fcf1e4] text-[#a66f36] px-2 py-[2px] rounded-md flex items-center gap-1 border border-[#fae5cd] text-[13px] font-bold tracking-wide">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="9" y1="15" x2="15" y2="9"></line>
                              <circle cx="9" cy="9" r="1.5" fill="currentColor" stroke="none"></circle>
                              <circle cx="15" cy="15" r="1.5" fill="currentColor" stroke="none"></circle>
                            </svg>
                            {promoCode || `PROMO`}
                          </div>
                          <div className="text-[14px] font-medium text-gray-700">
                            Promo discount
                          </div>
                        </div>
                        <div className="text-[16px] font-bold text-[#428514]">
                          -₹ {promoAmount.toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}



              {/* GST on Platform Fee */}
              {bookingData?.rc_platform_fee > 0 && bookingData?.rc_gst_rate > 0 && (
                <div className="flex justify-between items-center text-[14px] font-medium text-[#475569]">
                  <span>GST ({bookingData.rc_gst_rate}%)</span>
                  <span><span className="text-green-600 font-bold mr-1">+</span>₹ {displayGstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</span>
                </div>
              )}

              <hr className="border-[#CBD5E1]" />
              <div className="flex justify-between items-center text-[20px] font-medium text-[#475569]">
                <span>Total Paid</span>
                <span className='font-bold'>₹ {(() => {
                  const offeredFare = Number(fare?.PublishedFare || 0);
                  const total = offeredFare + effectivePlatformFee + displayGstAmount - remainingDiscountForFlight;
                  return total.toLocaleString('en-IN', { maximumFractionDigits: 2 });
                })()}</span>
              </div>
              <div className="pt-4 space-y-1">
                <div className="text-sm font-normal text-[#475569]">
                  Payment Method: {(() => {
                    const paymentMeta = safeParse(bookingData?.payment_meta);
                    const method = paymentMeta?.method;
                    if (method === 'card') return 'Card';
                    if (method === 'wallet') return `Wallet${paymentMeta?.wallet ? ` (${paymentMeta.wallet})` : ''}`;
                    if (method === 'upi') return `UPI${paymentMeta?.vpa ? ` (${paymentMeta.vpa})` : ''}`;
                    if (method === 'netbanking') return `Net Banking${paymentMeta?.bank ? ` (${paymentMeta.bank})` : ''}`;
                    return method || 'N/A';
                  })()}
                </div>
                <div className="text-sm font-normal text-[#475569]">Transaction ID: {bookingData?.payment_id || 'N/A'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="lg:col-span-5 space-y-8">
          {/* Passenger Details */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            <div className="bg-[#FFF9F5] px-6 py-5 flex items-center gap-3">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 8.5C17 5.73858 14.7614 3.5 12 3.5C9.23858 3.5 7 5.73858 7 8.5C7 11.2614 9.23858 13.5 12 13.5C14.7614 13.5 17 11.2614 17 8.5Z" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M19 20.5C19 16.634 15.866 13.5 12 13.5C8.13401 13.5 5 16.634 5 20.5" stroke="#FF4E01" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>

              <h2 className="text-lg font-semibold text-[#FF4E01]">Passenger Details</h2>
            </div>

            <div className="p-4 md:p-8 space-y-6">
              {passengers.map((passenger, idx) => (
                <div key={idx} className={`${idx !== 0 ? 'border-t border-gray-50 pt-6' : ''}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-orange-50 rounded-full flex items-center justify-center text-orange-600 font-semibold shadow-sm">
                        {idx + 1}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[17px] font-semibold text-[#0F172A]">{passenger?.Title} {passenger?.FirstName} {passenger?.LastName}</span>
                        <div className="flex items-baseline gap-2 mt-1">
                          <span className="text-xs font-semibold uppercase text-orange-500 bg-orange-50 px-2 py-0.5 rounded-full w-fit">
                            {getPaxTypeLabel(passenger?.PaxType)}
                          </span>
                          {passenger?.Gender && (
                            <span className="text-xs text-slate-400 font-medium italic">
                              • {passenger.Gender === 1 ? 'Male' : passenger.Gender === 2 ? 'Female' : ''}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {passenger.IsLeadPax && (
                        <span className="text-[11px] font-bold uppercase tracking-wider text-green-600 bg-green-50 px-2 py-1 rounded-md border border-green-100">
                          Lead Passenger
                        </span>
                      )}
                      {passenger?.TicketNumber && (
                        <div className="text-[12px] font-mono font-bold text-slate-600 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                          Tkt: {passenger.TicketNumber}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 ml-0 md:ml-14 mt-4 md:mt-0">
                    {/* DOB */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Date of Birth</span>
                        <span className="text-[14px] font-medium text-[#475569]">{passenger?.DateOfBirth ? formatFullDate(passenger.DateOfBirth) : 'N/A'}</span>
                      </div>
                    </div>

                    {/* Email */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9333EA" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                      </div>
                      <div className="flex flex-col overflow-hidden">
                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Email</span>
                        <span className="text-[14px] font-medium text-[#475569] truncate" title={passenger?.Email || displayEmail}>{passenger?.Email || (passenger.IsLeadPax ? displayEmail : 'N/A')}</span>
                      </div>
                    </div>

                    {/* Mobile */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-green-50 flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                          <line x1="12" y1="18" x2="12.01" y2="18" />
                        </svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Mobile Number</span>
                        <span className="text-[14px] font-medium text-[#475569]">{passenger?.ContactNo || (passenger.IsLeadPax ? displayPhone : 'N/A')}</span>
                      </div>
                    </div>

                    {/* Gender (Alternative display) */}
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-pink-50 flex items-center justify-center flex-shrink-0">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#DB2777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
                          <path d="M12 8l0 4" />
                          <path d="M12 12l0 4" />
                        </svg>
                      </div>
                      <div className="flex flex-col">
                        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Gender</span>
                        <span className="text-[14px] font-medium text-[#475569]">{passenger.Gender === 1 ? 'Male' : passenger.Gender === 2 ? 'Female' : 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payment Summary was here, now moved to left column */}
        </div>
      </div>

      {/* Booking Options */}
      <div className="space-y-6 pt-5 bg-white pb-8 rounded-xl">
        <h3 className="text-center text-gray-400 text-base font-bold  tracking-widest">Booking options</h3>
        <div className="flex flex-wrap justify-center gap-4">
          <button
            onClick={handleDownloadTicket}
            disabled={isDownloading}
            className={`px-6 py-3 rounded-lg text-xs font-medium transition-all flex items-center gap-2 whitespace-nowrap shadow-sm hover:translate-y-[-1px] active:scale-95 border group ${isDownloading
              ? 'bg-gray-100 text-[#475569] border-gray-200 cursor-not-allowed'
              : 'bg-white text-[#475569] border-gray-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A]'
              }`}
          >
            {isDownloading ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
            )}
            Download Ticket
          </button>

          <button
            onClick={handleDownloadInvoice}
            disabled={isGeneratingInvoice}
            className={`px-6 py-3 rounded-lg text-xs font-medium transition-all flex items-center gap-2 whitespace-nowrap shadow-sm hover:translate-y-[-1px] active:scale-95 border group ${isGeneratingInvoice
              ? 'bg-gray-100 text-[#475569] border-gray-200 cursor-not-allowed opacity-50'
              : 'bg-white text-[#475569] border-gray-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A]'
              }`}
          >
            {isGeneratingInvoice ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg className="w-4 h-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            )}
            Download Invoice
          </button>

          <button
            onClick={() => navigate('/booking-history')}
            className="px-6 py-3 bg-white text-[#475569] border border-gray-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] rounded-lg text-xs font-medium transition-all flex items-center gap-2 whitespace-nowrap shadow-sm hover:translate-y-[-1px] active:scale-95"
          >
            <svg className="w-4 h-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            Manage Booking
          </button>

          <button
            onClick={handleAddToCalendar}
            className="px-6 py-3 bg-white text-[#475569] border border-gray-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] rounded-lg text-xs font-medium transition-all flex items-center gap-2 whitespace-nowrap shadow-sm hover:translate-y-[-1px] active:scale-95"
          >
            <svg className="w-4 h-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Add to Calendar
          </button>

          <button
            onClick={() => setShowSupportModal(true)}
            className="px-6 py-3 bg-white text-[#475569] border border-gray-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] rounded-lg text-xs font-medium transition-all flex items-center gap-2 whitespace-nowrap shadow-sm hover:translate-y-[-1px] active:scale-95"
          >
            <svg className="w-4 h-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Contact Support
          </button>

          <button
            onClick={handleShareBooking}
            className="px-6 py-3 bg-white text-[#475569] border border-gray-200 hover:bg-[#0F172A] hover:text-white hover:border-[#0F172A] rounded-lg text-xs font-medium transition-all flex items-center gap-2 whitespace-nowrap shadow-sm hover:translate-y-[-1px] active:scale-95"
          >
            <svg className="w-4 h-4 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            Share Booking
          </button>
        </div>
      </div>

      {/* Complete Your Trip */}
      {/* <div className="space-y-8 pt-8 pb-10 bg-white">
        <h3 className="text-center text-gray-400 text-base font-bold tracking-wide">Complete Your Trip</h3>
        <div className="flex flex-wrap justify-center gap-6 px-4 max-w-5xl mx-auto">
         
          <div className="bg-white rounded-xl border border-[slate-200] py-6 px-16 text-center flex-1 min-w-[200px] flex flex-col items-center gap-4 transition-all cursor-pointer hover:border-slate-300">
            <div className="w-12 h-12 bg-[#E6F4EA] rounded-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="7" y="5" width="10" height="14" rx="2" stroke="#00875A" stroke-width="1.5" />
                <line x1="10" y1="15" x2="14" y2="15" stroke="#00875A" stroke-width="1.5" stroke-linecap="round" />
              </svg>
            </div>
            <h4 className="font-semibold text-[#0F172A] text-[16px]">Book Hotel</h4>
          </div>

        
          <div className="bg-white rounded-xl border border-slate-200 py-6 px-16 text-center flex-1 min-w-[200px] flex flex-col items-center gap-4 transition-all cursor-pointer hover:border-slate-300">
            <div className="w-12 h-12 bg-[#E6F4EA] rounded-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="#00875A" stroke-width="1.5" />
                <path d="M10.5 13.5L14.5 9.5M10.5 13.5L11.5 10.5L14.5 9.5L13.5 12.5L10.5 13.5Z" stroke="#00875A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <h4 className="font-semibold text-[#0F172A] text-[16px]">Rent Taxi</h4>
          </div>

         
          <div className="bg-white rounded-xl shadow-none border border-slate-200 py-6 px-16 text-center flex-1 min-w-[200px] flex flex-col items-center gap-4 transition-all cursor-pointer hover:border-slate-300">
            <div className="w-12 h-12 bg-[#E6F4EA] rounded-full flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="9" stroke="#00875A" stroke-width="1.5" />
                <path d="M10.5 13.5L14.5 9.5M10.5 13.5L11.5 10.5L14.5 9.5L13.5 12.5L10.5 13.5Z" stroke="#00875A" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </div>
            <h4 className="font-semibold text-[#0F172A] text-[16px]">Rent Bus</h4>
          </div>
        </div>
      </div> */}
      <ContactSupportModal
        isOpen={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        bookingId={bookingData?.rc_booking_id}
        prefillData={{
          firstName: leadPassenger?.FirstName || '',
          lastName: leadPassenger?.LastName || '',
          email: displayEmail !== 'N/A' ? displayEmail : '',
          phone: displayPhone !== 'N/A' ? displayPhone : '',
          message: `I need help with my booking from ${firstSegment?.Origin?.Airport?.CityName || 'Origin'} to ${lastSegment?.Destination?.Airport?.CityName || 'Destination'} (PNR: ${pnr || 'N/A'}).`
        }}
      />
    </div>
  )
}

BookingConfirmation.propTypes = {
  onContactSupport: PropTypes.func,
  trace_id: PropTypes.string,
  result_idx: PropTypes.string,
  flightDetails: PropTypes.object,
  bookingData: PropTypes.object,
};

export default BookingConfirmation
